import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Transition } from 'react-transition-group';
import { Check, ChevronDown, ChevronLeft, Volume2 } from 'lucide-react';
import PROXI_BRAIN_LOGO from '@/ui/assets/images/proxivideobrainlogo.png';
import { LANGUAGES } from '@/lib/constants';
import { LightShell, LightMark, LightSpinner } from '../light-ui';
import { useStartingPhrase } from '../thinkingPhrases';

interface WakeOverlayProps {
  inProp: boolean;
  loading: boolean;
  /** Start was tapped and the interview is getting ready: the spinner says so. */
  starting?: boolean;
  wake: () => void;
  adminName?: string;
  /** Avatar URL of the invitation host. Falls back to the brain logo when
   *  absent or when the image fails to load. */
  adminAvatar?: string;
  contributorName?: string;
  /** ProxiCastSeries title for the invitation's proxicast, when present.
   *  Surfaced on the start card so the contributor knows which show this
   *  interview belongs to. Falls back to `circleName` if absent; both
   *  absent → nothing is rendered. */
  seriesName?: string;
  circleName?: string;
  /** Fun & Games: the game's name (e.g. Story Dice) when this interview is a
   *  game. The card then says they have a game, not an interview. */
  game?: string;
  currentLanguage?: string;
  onLanguageChange?: (languageCode: string) => void;
  /** When provided, shows a "Back to interviews" chip in the top-left. */
  onBackToInterviews?: () => void;
  /** News-tip quick upload: when provided, renders the "skip interview &
   *  just upload files" link below the CTA. Only self-created news tips
   *  pass this. */
  onQuickUpload?: () => void;
  /** Files already quick-uploaded this session. > 0 switches the card into
   *  its "files sent" state (confirmation line + softer interview pitch). */
  quickUploadedCount?: number;
}

const WakeOverlay: React.FC<WakeOverlayProps> = ({
  loading,
  starting = false,
  inProp,
  wake,
  adminName = 'Admin',
  adminAvatar,
  contributorName = 'You',
  seriesName,
  circleName,
  game,
  currentLanguage = 'ENG',
  onLanguageChange,
  onBackToInterviews,
  onQuickUpload,
  quickUploadedCount = 0,
}) => {
  const contextLabel = (seriesName && seriesName.trim()) || (circleName && circleName.trim()) || '';
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const useHostAvatar = !!adminAvatar && !avatarFailed;
  const gameName = game?.trim() || '';
  const startingPhrase = useStartingPhrase(starting, !!gameName);
  const kind = gameName ? 'a game' : 'an interview';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };
    if (showLanguageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageDropdown]);

  const headingPrefix = useMemo(() => {
    if (!contributorName) return '';
    const lower = contributorName.trim().toLowerCase();
    if (lower === 'you' || lower === 'participant') return '';
    return contributorName.trim();
  }, [contributorName]);

  return (
    <Transition in={inProp} timeout={540} unmountOnExit>
      {(state) => {
        const entered = state === 'entering' || state === 'entered';
        // Once the slide-in has fully completed, drop the transform/transition
        // entirely so the browser releases the GPU compositing layer that
        // would otherwise keep the heading rendering with the GPU's greyscale
        // antialiasing instead of subpixel — that's what makes the J in "Josh
        // from Proxi" read as warped after the card settles.
        const atRest = state === 'entered';
        return (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1000,
              overflow: 'hidden',
              ...(atRest
                ? {}
                : {
                    transition: 'transform 320ms cubic-bezier(.4,0,.2,1)',
                    transform: `translateY(${entered ? '0%' : '100%'})`,
                  }),
            }}
          >
            <LightShell scroll>
              {/* Back to interviews — floats top-left, only when logged-in
                  parent wires up the navigation. */}
              {!loading && onBackToInterviews && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
                    left: 16,
                    zIndex: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={onBackToInterviews}
                    className="lp-frost-chip"
                    style={{ cursor: 'pointer', padding: '6px 10px 6px 8px', gap: 4 }}
                    aria-label="Back to interviews"
                  >
                    <ChevronLeft style={{ width: 13, height: 13 }} />
                    <span>Interviews</span>
                  </button>
                </div>
              )}

              {/* Language selector — floats top-right, outside the card */}
              {!loading && (
                <div
                  ref={dropdownRef}
                  style={{
                    position: 'absolute',
                    top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
                    right: 16,
                    zIndex: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowLanguageDropdown(v => !v)}
                    className="lp-frost-chip"
                    style={{ cursor: 'pointer', padding: '6px 10px' }}
                    aria-haspopup="listbox"
                    aria-expanded={showLanguageDropdown}
                  >
                    <span>{currentLanguage}</span>
                    <ChevronDown
                      style={{
                        width: 13, height: 13,
                        transition: 'transform 180ms ease',
                        transform: showLanguageDropdown ? 'rotate(180deg)' : 'rotate(0)',
                      }}
                    />
                  </button>
                  {showLanguageDropdown && (
                    <div
                      role="listbox"
                      className="lp-card lp-card-tight"
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: 0,
                        width: 180,
                        maxHeight: 240,
                        overflowY: 'auto',
                        padding: 4,
                      }}
                    >
                      {LANGUAGES.map(lang => {
                        const selected = currentLanguage === lang.code;
                        return (
                          <button
                            key={lang.code}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={() => {
                              onLanguageChange?.(lang.code);
                              setShowLanguageDropdown(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 10px',
                              border: 0,
                              borderRadius: '2px 10px 2px 10px',
                              background: selected ? 'rgba(236,72,153,0.10)' : 'transparent',
                              color: selected ? 'var(--lp-ink)' : 'var(--lp-ink-soft)',
                              fontFamily: 'var(--lp-mono)',
                              fontSize: 12,
                              letterSpacing: '0.06em',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            <span>{lang.label}</span>
                            <span style={{ fontSize: 10, color: 'var(--lp-ink-mute)' }}>{lang.code}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <main
                style={{
                  minHeight: '100dvh',
                  display: 'grid',
                  placeItems: 'center',
                  padding: 'calc(env(safe-area-inset-top, 0px) + 24px) clamp(16px, 4vw, 32px) calc(env(safe-area-inset-bottom, 0px) + 32px)',
                }}
              >
                <div
                  className="lp-card"
                  style={{
                    width: '100%',
                    maxWidth: 440,
                    padding: 'clamp(32px, 5.5vw, 44px) clamp(28px, 5vw, 40px)',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 18,
                    opacity: atRest ? 1 : entered ? 1 : 0,
                    ...(atRest
                      ? {}
                      : {
                          transform: entered
                            ? 'translateY(0) scale(1)'
                            : 'translateY(20px) scale(0.985)',
                          transition:
                            'opacity 540ms ease, transform 540ms cubic-bezier(.22,.61,.36,1)',
                        }),
                  }}
                >
                  {useHostAvatar ? (
                    <span
                      className="lp-mark"
                      style={{ width: 64, height: 64, overflow: 'hidden' }}
                      aria-label={adminName}
                    >
                      <img
                        src={adminAvatar}
                        alt={adminName}
                        onError={() => setAvatarFailed(true)}
                        // Absolute-fill mirrors the host avatar on the main
                        // screen — percentage sizing could leave a light band
                        // at the top of the circle on iOS Safari.
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block', opacity: 1 }}
                      />
                    </span>
                  ) : (
                    <LightMark src={PROXI_BRAIN_LOGO} size={64} imgScale={0.72} />
                  )}

                  {/* Show / series / circle context — only when there's a real
                      name to display. Generic fallback chip is dropped to
                      reduce visual noise. */}
                  {contextLabel && (
                    <span className="lp-frost-chip" style={{ marginTop: -4 }}>{contextLabel}</span>
                  )}

                  <h1
                    className="lp-section-heading"
                    style={{ fontSize: 'clamp(26px, 4.2vw, 34px)', marginTop: 2, lineHeight: 1.1 }}
                  >
                    {headingPrefix ? <>{headingPrefix}, you have {kind}.</> : `You have ${kind}.`}
                  </h1>

                  <p
                    className="lp-prose"
                    style={{ fontSize: 15, maxWidth: 320, margin: 0 }}
                  >
                    {quickUploadedCount > 0 ? (
                      <>Want to add context? Start the interview.</>
                    ) : gameName ? (
                      <>
                        A quick game of{' '}
                        <span style={{ color: 'var(--lp-ink)', fontStyle: 'italic' }}>{gameName}</span>{' '}
                        with{' '}
                        <span style={{ color: 'var(--lp-ink)', fontStyle: 'italic' }}>{adminName}</span>.
                      </>
                    ) : (
                      <>
                        A quick convo with{' '}
                        <span style={{ color: 'var(--lp-ink)', fontStyle: 'italic' }}>{adminName}</span>.
                      </>
                    )}
                  </p>

                  {/* Primary action — single CTA (or spinner) */}
                  <div style={{ marginTop: 12, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    {loading ? (
                      <>
                        <LightSpinner size={40} />
                        {starting ? (
                          <span
                            aria-live="polite"
                            style={{
                              fontFamily: 'var(--lp-mono)',
                              fontSize: 11,
                              letterSpacing: '0.04em',
                              color: 'var(--lp-ink-mute)',
                            }}
                          >
                            {startingPhrase}…
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <>
                        {quickUploadedCount > 0 && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              fontFamily: 'var(--lp-mono)',
                              fontSize: 11,
                              letterSpacing: '0.04em',
                              color: 'var(--lp-ink)',
                            }}
                          >
                            <Check style={{ width: 13, height: 13, color: '#16a34a' }} aria-hidden />
                            {quickUploadedCount} {quickUploadedCount === 1 ? 'file' : 'files'} sent to{' '}
                            {circleName || 'the circle'}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={wake}
                          aria-label={gameName ? 'Start game' : 'Start interview'}
                          className="lp-btn lp-btn-primary"
                          style={{ width: '100%', padding: '15px 22px', fontSize: 12 }}
                        >
                          {gameName ? 'Start game' : 'Start interview'}
                        </button>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontFamily: 'var(--lp-mono)',
                            fontSize: 11,
                            letterSpacing: '0.04em',
                            color: 'var(--lp-ink-mute)',
                          }}
                        >
                          <Volume2 style={{ width: 13, height: 13 }} aria-hidden />
                          Sound &amp; ringer on for the call
                        </span>
                        {onQuickUpload && (
                          <button
                            type="button"
                            onClick={onQuickUpload}
                            style={{
                              background: 'none',
                              border: 0,
                              padding: 0,
                              cursor: 'pointer',
                              fontFamily: 'var(--lp-mono)',
                              fontSize: 11,
                              letterSpacing: '0.04em',
                              color: 'var(--lp-ink-mute)',
                              textDecoration: 'underline',
                              textUnderlineOffset: 3,
                            }}
                          >
                            {quickUploadedCount > 0
                              ? 'upload more files'
                              : 'or skip interview & just upload files'}
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {!onBackToInterviews && (
                    <div style={{
                      marginTop: 10,
                      paddingTop: 16,
                      borderTop: '1px solid var(--lp-line)',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                      fontFamily: 'var(--lp-mono)',
                      fontSize: 10,
                      letterSpacing: '0.08em',
                      color: 'var(--lp-ink-mute)',
                      lineHeight: 1.5,
                    }}>
                      <span>
                        Powered by{' '}
                        <a href="https://www.ProxiAIStudio.com" target="_blank" rel="noopener noreferrer" className="lp-link">Proxi Studio</a>
                      </span>
                      <span>
                        By continuing you agree to our{' '}
                        <a href="https://www.proxiaistudio.com/terms" target="_blank" rel="noopener noreferrer" className="lp-link">Terms</a>{' '}
                        &amp;{' '}
                        <a href="https://www.proxiaistudio.com/privacy" target="_blank" rel="noopener noreferrer" className="lp-link">Privacy</a>
                      </span>
                    </div>
                  )}
                </div>
              </main>
            </LightShell>
          </div>
        );
      }}
    </Transition>
  );
};

export default WakeOverlay;
